import { CDL_V3_EVENT } from '@makemydeal/dr-shared-types';

// CDL Event payload structure
interface CDLEventConfig {
    tagIndex: string;
    element: string;
    action: string;
    intention: string;
    location: string;
}

interface CDLEvent {
    type: typeof CDL_V3_EVENT;
    payload: CDLEventPayload;
}

interface CDLEventPayload extends CDLEventConfig {
    buildOptions?: Record<string, unknown>;
}

// Generic CDL event creator
export const createCDLEvent = (eventConfig: CDLEventConfig, additionalData: Record<string, unknown> = {}): CDLEvent => {
    const eventData: CDLEventPayload = {
        ...eventConfig,
        ...additionalData
    };

    return {
        type: CDL_V3_EVENT,
        payload: eventData
    };
};

/**
 * dc-mv-130: Select this Vehicle button (from build results)
 */
export const createBrowseTemplatesEvent = () => {
    return createCDLEvent(
        {
            element: 'Start Deal Template Selection Icon',
            action: 'Click',
            intention: 'Browse Deal Templates',
            location: 'Deal Dashboard',
            tagIndex: 'dc-mv-130'
        },
        {}
    );
};

/**
 * dc-mv-131: Select deal template from template list
 */
export const createApplyTemplateEvent = (options: Record<string, unknown>, text?: string) => {
    return createCDLEvent(
        {
            element: 'Deal Template List Item',
            action: 'Click',
            intention: 'Select deal template',
            location: 'Deal Dashboard',
            tagIndex: 'dc-mv-131'
        },
        { buildOptions: options, ...(text && { text }) }
    );
};

/**
 * dc-mv-134: Manage Deal Templates Button click
 */
export const createManageTemplatesEvent = () => {
    return createCDLEvent(
        {
            element: 'Manage Deal Templates Button',
            action: 'Click',
            intention: 'Manage existing deal templates',
            location: 'Deal Dashboard',
            tagIndex: 'dc-mv-134'
        },
        {}
    );
};

/**
 * dc-mv-298: Save New Template Button click
 */
export const createSaveNewTemplateEvent = () => {
    return createCDLEvent(
        {
            element: 'Save New Template Button',
            action: 'Click',
            intention: 'Create template in Manager View',
            location: 'Deal Dashboard',
            tagIndex: 'dc-mv-298'
        },
        {}
    );
};

/**
 * dc-mv-204: Open proposal printing settings
 */
export const createOpeningProposalSettingsEvent = (options: Record<string, unknown>, isLite = false) => {
    return createCDLEvent(
        {
            element: 'Proposal Printing Settings Icon',
            action: 'Click',
            intention: 'Open proposal printing settings',
            location: `${isLite ? 'Deal Dashboard Lite' : 'Deal Dashboard'}, Footer`,
            tagIndex: 'dc-mv-204'
        },
        { buildOptions: options }
    );
};

/**
 * dc-mv-205: Print selected proposal
 */
export const createPrintingSelectedProposalEvent = (options: Record<string, unknown>, isLite = false) => {
    return createCDLEvent(
        {
            element: 'Print Proposal Button',
            action: 'Click',
            intention: 'Print selected proposal',
            location: `${isLite ? 'Deal Dashboard Lite' : 'Deal Dashboard'}`,
            tagIndex: 'dc-mv-205'
        },
        { buildOptions: options }
    );
};

/**
 * dc-mv-206: Add printing proposal
 */
export const createAddingProposalVersionEvent = (options: Record<string, unknown>, isLite = false) => {
    return createCDLEvent(
        {
            element: 'Add Printing Proposal Button',
            action: 'Click',
            intention: 'Add proposal version with new settings',
            location: `${isLite ? 'Deal Dashboard Lite' : 'Deal Dashboard'}`,
            tagIndex: 'dc-mv-206'
        },
        { buildOptions: options }
    );
};

/**
 * dc-mv-207: Select printing proposal Link
 */
export const createSelectingProposalForEditingEvent = (options: Record<string, unknown>, isLite = false) => {
    return createCDLEvent(
        {
            element: 'Select Printing Proposal Link',
            action: 'Click',
            intention: 'Select proposal to edit print settings',
            location: `${isLite ? 'Deal Dashboard Lite' : 'Deal Dashboard'}`,
            tagIndex: 'dc-mv-207'
        },
        { buildOptions: options }
    );
};

/**
 * dc-mv-208: Save and Close Proposal Settings Button
 */
export const createSavingProposalSettingsEvent = (options: Record<string, unknown>, isLite = false) => {
    return createCDLEvent(
        {
            element: 'Save and Close Proposal Settings Button',
            action: 'Click',
            intention: 'Save proposal settings',
            location: `${isLite ? 'Deal Dashboard Lite' : 'Deal Dashboard'}`,
            tagIndex: 'dc-mv-208'
        },
        { buildOptions: options }
    );
};

/**
 * dc-mv-209: Save new proposal version and initiate printing
 */
export const createSavingAndPrintingProposalEvent = (options: Record<string, unknown>, isLite = false) => {
    return createCDLEvent(
        {
            element: 'Save and Print Button',
            action: 'Click',
            intention: 'Save new proposal version and initiate printing',
            location: `${isLite ? 'Deal Dashboard Lite' : 'Deal Dashboard'}`,
            tagIndex: 'dc-mv-209'
        },
        { buildOptions: options }
    );
};

/**
 * dc-mv-220: Email printing proposal
 */
export const createEmailPrintingProposalEvent = (options: Record<string, unknown>, isLite = false) => {
    return createCDLEvent(
        {
            element: 'Email Proposal Button',
            action: 'Click',
            intention: 'Send proposal information to customer with email',
            location: `${isLite ? 'Deal Dashboard Lite' : 'Deal Dashboard'}`,
            tagIndex: 'dc-mv-220'
        },
        { buildOptions: options }
    );
};

/**
 * dc-mv-221: Text printing proposal
 */
export const createTextPrintingProposalEvent = (options: Record<string, unknown>, isLite = false) => {
    return createCDLEvent(
        {
            element: 'Text Proposal Button',
            action: 'Click',
            intention: 'Send proposal information to customer with text message',
            location: `${isLite ? 'Deal Dashboard Lite' : 'Deal Dashboard'}`,
            tagIndex: 'dc-mv-221'
        },
        { buildOptions: options }
    );
};

/**
 * dc-mv-252: Copy print proposal structure
 */
export const createCopyPrintProposalEvent = (options: Record<string, unknown>, isLite = false) => {
    return createCDLEvent(
        {
            element: 'Copy Print Proposal',
            action: 'Click',
            intention: 'Copy print proposal structure',
            location: `${isLite ? 'Deal Dashboard Lite' : 'Deal Dashboard'}`,
            tagIndex: 'dc-mv-252'
        },
        { buildOptions: options }
    );
};

/**
 * dc-mv-294: Select Four Square print layout
 */
export const createFourSquarePrintLayoutSelectedEvent = (options: Record<string, unknown>, isLite = false) => {
    return createCDLEvent(
        {
            element: 'Four Square Printing Proposal Button',
            action: 'Click',
            intention: 'Select Four Square print layout',
            location: `${isLite ? 'Deal Dashboard Lite' : 'Deal Dashboard'}`,
            tagIndex: 'dc-mv-294'
        },
        { buildOptions: options }
    );
};
